import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useUserContext } from "./UserContext";
import { fetchAppConfig, ProductosConfigListas } from "../services/configService";

type HeaderMapper = {
  [key: string]: { Header: string; Editable?: boolean; IsNumber?: boolean; Currency?: boolean };
};

interface AppConfigContextType {
  config: ProductosConfigListas | undefined;
  loading: boolean;
  refreshConfig: () => Promise<void>;
  productoVentaHeaders: HeaderMapper;
}

const CONFIG_STORAGE_KEY = "genfe_app_config";

const defaultPriceHeaders = {
  CONTADO: "CONTADO",
  MAYOR: "MAYOR",
  MENOR: "MENOR"
};

const AppConfigContext = createContext<AppConfigContextType | undefined>(undefined);

export const useAppConfig = (): AppConfigContextType => {
  const context = useContext(AppConfigContext);
  if (!context) {
    throw new Error("useAppConfig must be used within an AppConfigProvider");
  }
  return context;
};

const readCachedConfig = (): ProductosConfigListas | undefined => {
  const raw = localStorage.getItem(CONFIG_STORAGE_KEY);
  if (!raw) {
    return undefined;
  }

  try {
    return JSON.parse(raw);
  } catch {
    localStorage.removeItem(CONFIG_STORAGE_KEY);
    return undefined;
  }
};

const normalizeHeaderKey = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase();

const addHeaderAlias = (headers: HeaderMapper, key: string, value: HeaderMapper[string]) => {
  headers[key.toUpperCase()] = value;
  headers[normalizeHeaderKey(key)] = value;
};

const getFieldValue = (source: any, field: string): string | undefined =>
  source?.[field] ?? source?.[field.toLowerCase()] ?? source?.[field.toUpperCase()];

const getConfiguredNamesByField = (nombreListas: string): Record<string, string> => {
  if (!nombreListas?.trim()) {
    return {};
  }

  try {
    const parsed = JSON.parse(nombreListas);
    if (Array.isArray(parsed)) {
      return parsed.reduce((acc: Record<string, string>, item: any, index: number) => {
        if (typeof item === "string") {
          const fallbackKey = ["CONTADO", "MAYOR", "MENOR"][index];
          if (fallbackKey) {
            acc[fallbackKey] = item;
          }
          return acc;
        }

        const campo = getFieldValue(item, "campo_en_bd");
        const nombre = getFieldValue(item, "nombre");
        if (campo && nombre) {
          acc[campo.toUpperCase()] = nombre;
        }

        return acc;
      }, {});
    }

    if (typeof parsed === "object" && parsed !== null) {
      return {
        CONTADO: parsed.contado ?? parsed.CONTADO ?? parsed.Contado,
        MAYOR: parsed.mayor ?? parsed.MAYOR ?? parsed.Mayor,
        MENOR: parsed.menor ?? parsed.MENOR ?? parsed.Menor
      };
    }
  } catch {
    // Fall back to common delimiters used in configuration text.
  }

  const values = nombreListas
    .split(/[|;,]/)
    .map((item) => item.trim())
    .filter(Boolean);

  return {
    CONTADO: values[0],
    MAYOR: values[1],
    MENOR: values[2]
  };
};

const buildProductoVentaHeaders = (config: ProductosConfigListas | undefined): HeaderMapper => {
  const configuredNames = getConfiguredNamesByField(config?.nombreListas ?? "");
  const contado = configuredNames.CONTADO || defaultPriceHeaders.CONTADO;
  const mayor = configuredNames.MAYOR || defaultPriceHeaders.MAYOR;
  const menor = configuredNames.MENOR || defaultPriceHeaders.MENOR;
  const headers: HeaderMapper = {
    CODIGO: { Header: "Codigo", Editable: false },
    PRODUCTO: { Header: "Producto", Editable: false }
  };

  addHeaderAlias(headers, "CONTADO", { Header: contado, Editable: false, IsNumber: true, Currency: true });
  addHeaderAlias(headers, contado, { Header: contado, Editable: false, IsNumber: true, Currency: true });
  addHeaderAlias(headers, "MAYOR", { Header: mayor, Editable: false, IsNumber: true, Currency: true });
  addHeaderAlias(headers, mayor, { Header: mayor, Editable: false, IsNumber: true, Currency: true });
  addHeaderAlias(headers, "MENOR", { Header: menor, Editable: false, IsNumber: true, Currency: true });
  addHeaderAlias(headers, menor, { Header: menor, Editable: false, IsNumber: true, Currency: true });

  return headers;
};

export const AppConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { getUser } = useUserContext();
  const [config, setConfig] = useState<ProductosConfigListas | undefined>(readCachedConfig);
  const [loading, setLoading] = useState(false);
  const user = getUser();
  const loadedTokenRef = useRef<string | undefined>(undefined);

  const loadConfig = useCallback(async (forceRefresh: boolean) => {
    const currentUser = getUser();
    if (!currentUser) {
      setConfig(undefined);
      localStorage.removeItem(CONFIG_STORAGE_KEY);
      loadedTokenRef.current = undefined;
      return;
    }

    if (!forceRefresh && loadedTokenRef.current === currentUser.token) {
      return;
    }

    setLoading(true);
    try {
      const nextConfig = await fetchAppConfig(currentUser);
      setConfig(nextConfig);
      loadedTokenRef.current = currentUser.token;
      if (nextConfig) {
        localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(nextConfig));
      } else {
        localStorage.removeItem(CONFIG_STORAGE_KEY);
      }
    } finally {
      setLoading(false);
    }
  }, [getUser]);

  const refreshConfig = useCallback(async () => {
    await loadConfig(true);
  }, [loadConfig]);

  useEffect(() => {
    if (user?.token) {
      loadConfig(false).catch((error) => console.error("Error loading app config:", error));
    } else {
      setConfig(undefined);
      loadedTokenRef.current = undefined;
    }
  }, [user?.token, loadConfig]);

  const productoVentaHeaders = useMemo(() => buildProductoVentaHeaders(config), [config]);

  return (
    <AppConfigContext.Provider value={{ config, loading, refreshConfig, productoVentaHeaders }}>
      {children}
    </AppConfigContext.Provider>
  );
};
